import React, { useState, useEffect } from 'react';
import { COVER_BG_IMAGE } from '../constants/cover';
import { teamsAPI, contentAPI } from '../services/api';
import { FaNewspaper, FaCalendar, FaEye, FaFutbol, FaFilter } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { useLanguage } from '../context/LanguageContext';

const ClubNewsPage = () => {
  const { language } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [news, setNews] = useState([]);

  useEffect(() => {
    loadData();
  }, [language]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsRes, newsRes] = await Promise.all([
        teamsAPI.getAll(),
        contentAPI.getClubNews()
      ]);

      setTeams(teamsRes.data || []);
      const newsData = newsRes.data || [];

      // Map news with team information
      const mappedNews = newsData.map(item => {
        const team = (teamsRes.data || []).find(t => t.id === item.team_id);
        return {
          ...item,
          teamName: team ? team.name : (language === 'ar' ? 'فريق' : 'Équipe'),
          teamLogo: team ? team.logo_url : null
        };
      });

      setNews(mappedNews);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const filteredNews = news.filter(n => {
    // Filtre par équipe
    if (selectedTeam !== 'all' && n.team_id !== parseInt(selectedTeam)) {
      return false;
    }

    // Filtre par catégorie
    if (selectedCategory !== 'all' && n.category !== selectedCategory) {
      return false;
    }

    // Filtre par recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const titleMatch = (n.title || '').toLowerCase().includes(query) || (n.title_ar || '').toLowerCase().includes(query);
      const contentMatch = (n.content || '').toLowerCase().includes(query) || (n.content_ar || '').toLowerCase().includes(query);
      return titleMatch || contentMatch || n.teamName.toLowerCase().includes(query);
    }

    return true;
  });

  // Obtenir toutes les catégories uniques
  const categories = [...new Set(news.map(n => n.category).filter(Boolean))];

  const text = {
    ar: {
      title: 'أخبار الأندية',
      subtitle: 'آخر أخبار وتحديثات الفرق المشاركة في البطولة',
      allTeams: 'جميع الفرق',
      allCategories: 'جميع الفئات',
      filterByTeam: 'تصفية حسب الفريق',
      filterByCategory: 'تصفية حسب الفئة',
      search: 'بحث في الأخبار...',
      totalNews: 'خبر',
      readMore: 'اقرأ المزيد',
      views: 'مشاهدة',
      noNews: 'لا توجد أخبار',
      noResults: 'لم يتم العثور على نتائج',
      clearFilters: 'مسح الفلاتر'
    },
    fr: {
      title: 'Actualités des Clubs',
      subtitle: 'Dernières nouvelles et mises à jour des équipes participantes au championnat',
      allTeams: 'Toutes les équipes',
      allCategories: 'Toutes les catégories',
      filterByTeam: 'Filtrer par équipe',
      filterByCategory: 'Filtrer par catégorie',
      search: 'Rechercher dans les actualités...',
      totalNews: 'Actualités',
      readMore: 'Lire plus',
      views: 'vues',
      noNews: 'Aucune actualité',
      noResults: 'Aucun résultat trouvé',
      clearFilters: 'Effacer les filtres'
    }
  };

  const t = text[language];

  return (
    <div style={styles.container}>
      {/* Header */}
      <section style={styles.header}>
        <h1 style={styles.title}>{t.title}</h1>
        <p style={styles.subtitle}>{t.subtitle}</p>
        <div style={styles.statsBar}>
          <div style={styles.stat}>
            <span style={styles.statNumber}>{filteredNews.length}</span>
            <span style={styles.statLabel}>{t.totalNews}</span>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section style={styles.filterSection}>
        <div style={styles.filterContainer}>
          {/* Search Bar */}
          <div style={styles.searchBox}>
            <input
              type="text"
              placeholder={t.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {/* Team Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>
              <FaFilter style={styles.filterIcon} />
              <span>{t.filterByTeam}</span>
            </label>
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">{t.allTeams}</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>
              <FaNewspaper style={styles.filterIcon} />
              <span>{t.filterByCategory}</span>
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">{t.allCategories}</option>
              {categories.map((cat, index) => (
                <option key={index} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          {(selectedTeam !== 'all' || selectedCategory !== 'all' || searchQuery) && (
            <button
              onClick={() => {
                setSelectedTeam('all');
                setSelectedCategory('all');
                setSearchQuery('');
              }}
              style={styles.clearBtn}
            >
              {t.clearFilters}
            </button>
          )}
        </div>
      </section>

      {/* News Grid */}
      <section style={styles.newsSection}>
        {filteredNews.length > 0 ? (
          <div style={styles.newsGrid}>
            {filteredNews.map((item, index) => (
              <div
                key={item.id}
                style={{
                  ...styles.newsCard,
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {/* Image */}
                <div style={styles.newsImage}>
                  <img
                    src={item.image_url || 'https://via.placeholder.com/800x600/c41e3a/ffffff?text=Football+News'}
                    alt={language === 'ar' ? (item.title_ar || item.title) : item.title}
                    style={styles.newsImg}
                  />
                  <div style={styles.newsCategory}>
                    <FaNewspaper style={styles.categoryIcon} />
                    {item.category || (language === 'ar' ? 'عام' : 'Général')}
                  </div>
                </div>

                {/* Content */}
                <div style={styles.newsContent}>
                  {/* Team Badge */}
                  <div style={styles.teamBadge}>
                    {item.teamLogo ? (
                      <img
                        src={item.teamLogo}
                        alt={item.teamName}
                        style={styles.teamBadgeLogo}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <FaFutbol style={styles.teamBadgeIcon} />
                    )}
                    <span style={styles.teamBadgeName}>{item.teamName}</span>
                  </div>

                  {/* Title */}
                  <h3 style={{ ...styles.newsTitle, direction: language === 'ar' ? 'rtl' : 'ltr', textAlign: language === 'ar' ? 'right' : 'left' }}>
                    {language === 'ar' ? (item.title_ar || item.title) : item.title}
                  </h3>

                  {/* Description */}
                  <p style={{ ...styles.newsDescription, direction: language === 'ar' ? 'rtl' : 'ltr', textAlign: language === 'ar' ? 'right' : 'left' }}>
                    {language === 'ar' ? (item.content_ar || item.content) : item.content}
                  </p>

                  {/* Meta */}
                  <div style={styles.newsMeta}>
                    <div style={styles.metaItem}>
                      <FaCalendar style={styles.metaIcon} />
                      <span>
                        {new Date(item.published_at || item.date).toLocaleDateString(
                          language === 'ar' ? 'ar-TN' : 'fr-FR',
                          { day: 'numeric', month: 'long', year: 'numeric' }
                        )}
                      </span>
                    </div>
                    <div style={styles.metaItem}>
                      <FaEye style={styles.metaIcon} />
                      <span>{item.views || 0} {t.views}</span>
                    </div>
                  </div>

                  {/* Read More Button */}
                  <button style={styles.readMoreBtn}>
                    {t.readMore} {language === 'ar' ? '→' : '←'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.noData}>
            <FaNewspaper style={styles.noDataIcon} />
            <h3>{searchQuery || selectedTeam !== 'all' || selectedCategory !== 'all' ? t.noResults : t.noNews}</h3>
            {(searchQuery || selectedTeam !== 'all' || selectedCategory !== 'all') && (
              <button
                onClick={() => {
                  setSelectedTeam('all');
                  setSelectedCategory('all');
                  setSearchQuery('');
                }}
                style={styles.clearBtn}
              >
                {t.clearFilters}
              </button>
            )}
          </div>
        )
        }
      </section >

      <Footer />
    </div >
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  header: {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.52) 0%, rgba(44, 82, 130, 0.48) 100%), url(${COVER_BG_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '80px 20px 60px',
    textAlign: 'center',
    color: 'white'
  },
  title: {
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '15px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
  },
  subtitle: {
    fontSize: '20px',
    marginBottom: '30px',
    opacity: 0.9,
    maxWidth: '800px',
    margin: '0 auto 30px'
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginTop: '30px'
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.2)',
    padding: '20px 40px',
    borderRadius: '15px',
    backdropFilter: 'blur(10px)'
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: 'bold'
  },
  statLabel: {
    fontSize: '16px',
    marginTop: '5px'
  },
  filterSection: {
    background: 'white',
    padding: '30px 20px',
    borderBottom: '2px solid #e0e0e0',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
  },
  filterContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'flex-end',
    gap: '20px',
    flexWrap: 'wrap'
  },
  searchBox: {
    flex: '1 1 300px',
    minWidth: '250px'
  },
  searchInput: {
    width: '100%',
    padding: '14px 20px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    background: 'white',
    transition: 'all 0.3s',
    fontWeight: '500'
  },
  filterGroup: {
    flex: '1 1 200px',
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333'
  },
  filterIcon: {
    color: '#c41e3a',
    fontSize: '16px'
  },
  filterSelect: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s',
    fontWeight: '600'
  },
  clearBtn: {
    padding: '12px 24px',
    background: '#c41e3a',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
    whiteSpace: 'nowrap'
  },
  newsSection: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '60px 20px'
  },
  newsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
    gap: '30px'
  },
  newsCard: {
    background: 'white',
    borderRadius: '15px',
    overflow: 'hidden',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    transition: 'all 0.3s',
    cursor: 'pointer',
    animation: 'fadeInUp 0.6s ease forwards',
    opacity: 0
  },
  newsImage: {
    position: 'relative',
    height: '250px',
    overflow: 'hidden'
  },
  newsImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s'
  },
  newsCategory: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    background: 'rgba(196, 30, 58, 0.95)',
    color: 'white',
    padding: '8px 15px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backdropFilter: 'blur(10px)'
  },
  categoryIcon: {
    fontSize: '12px'
  },
  newsContent: {
    padding: '25px'
  },
  teamBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '15px',
    padding: '10px',
    background: '#f9f9f9',
    borderRadius: '10px'
  },
  teamBadgeLogo: {
    width: '35px',
    height: '35px',
    objectFit: 'contain'
  },
  teamBadgeIcon: {
    fontSize: '30px',
    color: '#c41e3a'
  },
  teamBadgeName: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333'
  },
  newsTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '15px',
    lineHeight: '1.4',
    minHeight: '56px'
  },
  newsDescription: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '20px',
    minHeight: '84px',
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  newsMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '15px',
    borderTop: '1px solid #f0f0f0',
    marginBottom: '15px'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#666'
  },
  metaIcon: {
    color: '#c41e3a',
    fontSize: '14px'
  },
  readMoreBtn: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s'
  },
  noData: {
    textAlign: 'center',
    padding: '80px 20px',
    color: '#999'
  },
  noDataIcon: {
    fontSize: '64px',
    marginBottom: '20px',
    opacity: 0.3
  }
};

// Ajouter l'animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .newsCard:hover .newsImg {
    transform: scale(1.05);
  }

  .newsCard:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }

  .readMoreBtn:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 15px rgba(196, 30, 58, 0.3);
  }

  .clearBtn:hover {
    background: #8b0000;
    transform: scale(1.05);
  }

  .searchInput:focus {
    outline: none;
    border-color: #c41e3a;
    box-shadow: 0 0 0 3px rgba(196, 30, 58, 0.1);
  }

  .filterSelect:focus {
    outline: none;
    border-color: #c41e3a;
    box-shadow: 0 0 0 3px rgba(196, 30, 58, 0.1);
  }

  .filterSelect:hover {
    border-color: #c41e3a;
  }

  @media (max-width: 768px) {
    .newsGrid {
      grid-template-columns: 1fr !important;
    }
    
    .filterContainer {
      flex-direction: column;
      align-items: stretch !important;
    }
    
    .searchBox, .filterGroup {
      flex: 1 1 100% !important;
      min-width: 100% !important;
    }
    
    .clearBtn {
      width: 100%;
    }
  }
`;
document.head.appendChild(styleSheet);

export default ClubNewsPage;
